import { useState, useEffect } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { docMgmtI18n } from '../../i18n';
import {
  getDocumentSendDetails,
  getDocumentSendView,
  putDocumentSendUpdateStatus,
} from '../../../../services/documentManagementServices';
import { formatDate } from '../../../../utils/formatDate';
import './modalSendingDetails.css';
import PdfViewer from '../../../../components/PdfViewer/PdfViewer';
import usePermissionsSmartDocuments from 'permissions/modules/smartDocuments/usePermissionsSmartDocuments';

const ModalSendingDetails = ({
  DesignSystemComponents,
  show,
  onClose,
  documentId,
  onSuccess,
  startGlobalLoading,
  stopGlobalLoading,
  userId,
}) => {
  const { Modal, Button, Alert, SvgIcon, Skeleton } = DesignSystemComponents;

  const { t } = useScopedTranslation(docMgmtI18n);

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [requestComment, setRequestComment] = useState('');

  const [showConfirm, setShowConfirm] = useState(false);

  const [loadingPdf, setLoadingPdf] = useState(true);

  const [pdfUrl, setPdfUrl] = useState(null);

  const { canManageAndAddComment } = usePermissionsSmartDocuments();

  useEffect(() => {
    if (!show || !documentId) return;

    setDetails(null);
    setPdfUrl(null);
    setLoading(true);
    setLoadingPdf(true);

    const fetchDetails = async () => {
      try {
        const result = await getDocumentSendDetails.getById(documentId);
        setDetails(result);

        const url = await getDocumentSendView.getViewUrl(documentId);
        setPdfUrl(url);
      } catch (error) {
        console.warn('Could not load detail:', error);
      } finally {
        setLoading(false);
        setLoadingPdf(false);
      }
    };

    fetchDetails();
  }, [show, documentId]);

  const handleAction = async (statusId) => {
    startGlobalLoading();

    try {
      await putDocumentSendUpdateStatus.putUpdateStatus({
        id: documentId,
        userId,
        statusId,
        observation: requestComment,
        updatedAt: new Date()
          .toLocaleString('sv-SE', { timeZone: 'America/Santiago' })
          .replace(' ', 'T'),
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.warn('Error updating status:', error);
    } finally {
      stopGlobalLoading();
    }
  };

  useEffect(() => {
    if (!show || !documentId) return;

    const fetchDetails = async () => {
      try {
        setLoading(true);

        const result = await getDocumentSendDetails.getById(documentId);
        setDetails(result);

        const url = await getDocumentSendView.getViewUrl(documentId);
        setPdfUrl(url);
      } catch (error) {
        console.warn('Could not load detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [show, documentId]);

  const ButtonsForModal = () => (
    <div className='d-flex justify-content-end'>
      <Button
        label={t('dm_detail_close')}
        onClick={onClose}
        size='sm'
        variant='secondary'
        className='me-3'
      />
      <Button
        label={t('dm_detail_reject')}
        onClick={() => handleAction(3)}
        size='sm'
        variant='error'
        className='me-3'
        disabled={requestComment.trim().length === 0 || !canManageAndAddComment}
      />
      <Button
        label={t('dm_detail_approve')}
        size='sm'
        variant='success'
        className='me-3'
        onClick={() => {
          if (details.deliveryTypeIdDocument === 2) {
            setShowConfirm(true);
          } else {
            handleAction(5);
          }
        }}
        disabled={!canManageAndAddComment}
      />
      <Button
        label={t('dm_detail_cancelRequest')}
        onClick={() => handleAction(4)}
        size='sm'
        variant='warning'
        disabled={!canManageAndAddComment}
      />
    </div>
  );

  useEffect(() => {
    if (!show) {
      setShowConfirm(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      setDetails(null);
      setPdfUrl(null);
      setRequestComment('');
      setShowConfirm(false);
    }
  }, [show]);

  return (
    <Modal
      title={
        !showConfirm
          ? t('dm_detail_title')
          : t('dm_detail_titleConfirm')
      }
      show={show}
      onClose={onClose}
      width={showConfirm ? 520 : 960}
      customFooter={!showConfirm ? <ButtonsForModal /> : null}
    >
      <div className={`modal-fade-wrapper ${showConfirm ? 'compact' : 'full'}`}>
        <div
          className={`modal-fade-section ${showConfirm ? 'hidden' : 'visible'}`}
        >
          {loading && (
            <div className='row'>
              <div className='col-12 col-md-6'>
                <Skeleton height='500px' />
              </div>
              <div className='col-12 col-md-6'>
                <Skeleton height='500px' />
              </div>
            </div>
          )}

          {!loading && details && (
            <div className='row'>
              <div className='col-8'>
                <p className='body  fw-semibold mb-3'>
                  {t('dm_detail_preview')}
                </p>
                {loadingPdf ? (
                  <Skeleton height='700px' />
                ) : pdfUrl ? (
                  <PdfViewer
                    pdf={pdfUrl}
                    DesignSystemComponents={DesignSystemComponents}
                    className='pdf-viewer-style-document'
                  />
                ) : (
                  <p className='body '>{t('dm_detail_notAvailable')}</p>
                )}
              </div>
              <div className='col-4'>
                <p className='body  fw-semibold mb-3'>
                  {t('dm_detail_infoTitle')}
                </p>
                <div className='row mb-3'>
                  <div className='col-12'>
                    <p className='mini mb-1 font-light'>{t('dm_detail_nameLabel')}</p>
                    <p className='caption'>{details.name}</p>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>{t('dm_detail_docTypeLabel')}</p>
                    <p className='caption'>{details.documentType}</p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>{t('dm_detail_serviceLabel')}</p>
                    <p className='caption'>{details.serviceName}</p>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-12'>
                    <p className='mini mb-1 font-light'>{t('dm_detail_caseLabel')}</p>
                    <p className='caption'>{details.caseTitle}</p>
                    <p className='mini mb-1 font-light'>
                      {t('dm_detail_roleLabel', { code: details.caseCode })}
                    </p>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>{t('dm_detail_userLabel')}</p>
                    <p className='caption'>{details.userName}</p>
                  </div>
                  <div className='col-6'>
                    <p className='mini mb-1 font-light'>{t('dm_detail_dateLabel')}</p>
                    <p className='caption'>{formatDate(details.createdAt)}</p>
                  </div>
                </div>
                <p className='body  fw-semibold mb-3'>{t('dm_detail_managementTitle')}</p>
                <div>
                  <label className='form-label'>{t('dm_detail_observationsLabel')}</label>
                  <textarea
                    className='form-control'
                    placeholder={t('dm_detail_observationsPlaceholder')}
                    rows={6}
                    value={requestComment}
                    onChange={(e) => setRequestComment(e.target.value)}
                    disabled={!canManageAndAddComment}
                  />
                </div>
                <Alert variant='warning' className='alert-on-modal'>
                  <div className='d-flex'>
                    <SvgIcon name='infoCircle' size='1.25rem' />
                    <div className='ms-2'>
                      <p className='caption '>
                        {t('dm_detail_importantNote')}
                      </p>
                    </div>
                  </div>
                </Alert>
              </div>
            </div>
          )}
        </div>
        <div
          className={`modal-fade-section ${showConfirm ? 'visible' : 'hidden'}`}
        >
          <p className='caption mb-3'>
            {t('dm_detail_approveMessage')}
          </p>
          <div className='mt-4 d-flex justify-content-end'>
            <Button
              label={t('dm_detail_approveCancel')}
              size='sm'
              variant='secondary'
              onClick={() => setShowConfirm(false)}
              className='me-3'
            />
            <Button
              label={t('dm_detail_approveConfirm')}
              size='sm'
              variant='success'
              onClick={() => handleAction(5)}
              disabled={!canManageAndAddComment}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalSendingDetails;
